import { useState, useMemo, useCallback, useEffect } from 'react'
import type {
  Case,
  CaseStatus,
  CreateCaseInput,
  UpdateCaseInput,
  ClientProfile,
} from '../../types/case-management'
import type { MyCaseStatistics } from '../../types/dashboard'
import {
  useCasesGrouped,
  useCaseMutations,
  useClientProfiles,
  useClientProfileMutations,
  useCaseQuota,
} from '../../hooks'

type ViewMode = 'work' | 'summary'
type StatusTab = 'active' | 'closed' | 'archived'

const VIEW_STORAGE_KEY = 'dashboard.analystView'
const TAB_STORAGE_KEY = 'dashboard.statusTab'
const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24
const MS_PER_DAY = MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY

export const activeStatuses: CaseStatus[] = ['collection', 'analysis', 'review', 'reporting']
export const closedStatuses: CaseStatus[] = ['closed']
export const archivedStatuses: CaseStatus[] = ['archived']

export type { ViewMode, StatusTab }

function readStoredViewMode(): ViewMode {
  const stored = localStorage.getItem(VIEW_STORAGE_KEY)
  return stored === 'summary' || stored === 'work' ? stored : 'work'
}

function readStoredStatusTab(): StatusTab {
  const stored = localStorage.getItem(TAB_STORAGE_KEY)
  return stored === 'active' || stored === 'closed' || stored === 'archived' ? stored : 'active'
}

function buildMyCaseStatistics(args: {
  allCases: Case[]
  casesByStatus: Record<CaseStatus, Case[]>
  activeCases: number
  casesLoading: boolean
}): MyCaseStatistics | null {
  const { allCases, casesByStatus, activeCases, casesLoading } = args
  if (allCases.length === 0 && !casesLoading) return null

  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  const closedThisMonth = allCases.filter(
    (item) => item.status === 'closed' && new Date(item.updatedAt) >= startOfMonth
  ).length

  const closedCases = allCases.filter((item) => item.status === 'closed')
  let avgDaysToClose: number | null = null
  if (closedCases.length > 0) {
    const totalDays = closedCases.reduce((sum, item) => {
      const days = Math.ceil(
        (new Date(item.updatedAt).getTime() - new Date(item.createdAt).getTime()) / MS_PER_DAY
      )
      return sum + days
    }, 0)
    avgDaysToClose = Math.round(totalDays / closedCases.length)
  }

  return {
    totalCases: allCases.length,
    activeCases,
    closedThisMonth,
    casesByStatus: {
      collection: casesByStatus.collection.length,
      analysis: casesByStatus.analysis.length,
      review: casesByStatus.review.length,
      reporting: casesByStatus.reporting.length,
      closed: casesByStatus.closed.length,
      archived: casesByStatus.archived.length,
    },
    casesByPriority: {
      critical: allCases.filter((item) => item.priority === 'critical').length,
      high: allCases.filter((item) => item.priority === 'high').length,
      medium: allCases.filter((item) => item.priority === 'medium').length,
      low: allCases.filter((item) => item.priority === 'low').length,
    },
    avgDaysToClose,
  }
}

export interface UseCaseDashboardReturn {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  statusTab: StatusTab
  setStatusTab: (tab: StatusTab) => void
  isNewCaseModalOpen: boolean
  setIsNewCaseModalOpen: (open: boolean) => void
  isEditCaseModalOpen: boolean
  setIsEditCaseModalOpen: (open: boolean) => void
  selectedCase: Case | null
  setSelectedCase: (c: Case | null) => void
  searchQuery: string
  setSearchQuery: (q: string) => void
  allCases: Case[]
  filteredCases: Case[]
  tabCounts: { active: number; closed: number; archived: number }
  stats: { active: number; critical: number; totalArtifacts: number }
  myCaseStatistics: MyCaseStatistics | null
  clients: ReturnType<typeof useClientProfiles>['profiles']
  quota: ReturnType<typeof useCaseQuota>['quota']
  casesLoading: boolean
  clientsLoading: boolean
  quotaLoading: boolean
  isLoading: boolean
  casesError: string | null
  creating: boolean
  updating: boolean
  creatingClient: boolean
  mutationError: string | null
  handleCreateCase: (data: CreateCaseInput) => Promise<void>
  handleCreateClient: (data: { name: string; shortName?: string }) => Promise<ClientProfile | null>
  handleOpenCase: (caseId: string) => void
  handleUpdateCase: (id: string, data: UpdateCaseInput) => Promise<void>
  handleRefresh: () => void
}

function useCaseDerivedData(args: {
  casesByStatus: Record<CaseStatus, Case[]>
  searchQuery: string
  statusTab: StatusTab
  casesLoading: boolean
}): {
  allCases: Case[]
  filteredCases: Case[]
  tabCounts: { active: number; closed: number; archived: number }
  stats: { active: number; critical: number; totalArtifacts: number }
  myCaseStatistics: MyCaseStatistics | null
} {
  const { casesByStatus, searchQuery, statusTab, casesLoading } = args

  const allCases = useMemo(
    () => [
      ...casesByStatus.collection,
      ...casesByStatus.analysis,
      ...casesByStatus.review,
      ...casesByStatus.reporting,
      ...casesByStatus.closed,
      ...casesByStatus.archived,
    ],
    [casesByStatus]
  )

  const filteredCases = useMemo(() => {
    const statusesToInclude =
      statusTab === 'active'
        ? activeStatuses
        : statusTab === 'closed'
          ? closedStatuses
          : archivedStatuses
    let tabCases: Case[] = []
    for (const status of statusesToInclude) tabCases = [...tabCases, ...casesByStatus[status]]
    if (!searchQuery.trim()) return tabCases
    const query = searchQuery.toLowerCase()
    return tabCases.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        (item.clientName ?? '').toLowerCase().includes(query) ||
        (item.orgCaseReference ?? '').toLowerCase().includes(query)
    )
  }, [casesByStatus, searchQuery, statusTab])

  const tabCounts = useMemo(
    () => ({
      active: activeStatuses.reduce((sum, status) => sum + casesByStatus[status].length, 0),
      closed: closedStatuses.reduce((sum, status) => sum + casesByStatus[status].length, 0),
      archived: archivedStatuses.reduce((sum, status) => sum + casesByStatus[status].length, 0),
    }),
    [casesByStatus]
  )

  const stats = useMemo(
    () => ({
      active: allCases.filter((item) => item.status !== 'closed' && item.status !== 'archived')
        .length,
      critical: allCases.filter(
        (item) =>
          item.priority === 'critical' && item.status !== 'closed' && item.status !== 'archived'
      ).length,
      totalArtifacts: allCases.reduce((sum, item) => sum + item.artifactCount, 0),
    }),
    [allCases]
  )

  const myCaseStatistics = useMemo(
    () =>
      buildMyCaseStatistics({ allCases, casesByStatus, activeCases: stats.active, casesLoading }),
    [allCases, casesByStatus, stats.active, casesLoading]
  )

  return { allCases, filteredCases, tabCounts, stats, myCaseStatistics }
}

interface CaseHandlerDeps {
  createCase: ReturnType<typeof useCaseMutations>['create']
  updateCase: ReturnType<typeof useCaseMutations>['update']
  createClient: ReturnType<typeof useClientProfileMutations>['create']
  refetchCases: () => Promise<void>
  refetchClients: () => Promise<void>
  refetchQuota: () => Promise<void>
  allCases: Case[]
  setIsNewCaseModalOpen: (open: boolean) => void
  setIsEditCaseModalOpen: (open: boolean) => void
  setSelectedCase: (c: Case | null) => void
}

function createCaseHandlers(deps: CaseHandlerDeps): {
  handleCreateCase: (data: CreateCaseInput) => Promise<void>
  handleCreateClient: (data: { name: string; shortName?: string }) => Promise<ClientProfile | null>
  handleOpenCase: (caseId: string) => void
  handleUpdateCase: (id: string, data: UpdateCaseInput) => Promise<void>
  handleRefresh: () => void
} {
  const handleCreateCase = async (data: CreateCaseInput): Promise<void> => {
    const result = await deps.createCase(data)
    if (result) {
      deps.setIsNewCaseModalOpen(false)
      void deps.refetchCases()
      void deps.refetchQuota()
    }
  }

  const handleCreateClient = async (data: {
    name: string
    shortName?: string
  }): Promise<ClientProfile | null> => {
    const result = await deps.createClient(data)
    if (result) void deps.refetchClients()
    return result
  }

  const handleOpenCase = (caseId: string): void => {
    const caseToEdit = deps.allCases.find((item) => item.id === caseId)
    if (caseToEdit) {
      deps.setSelectedCase(caseToEdit)
      deps.setIsEditCaseModalOpen(true)
    }
  }

  const handleUpdateCase = async (id: string, data: UpdateCaseInput): Promise<void> => {
    const result = await deps.updateCase(id, data)
    if (result) {
      deps.setIsEditCaseModalOpen(false)
      deps.setSelectedCase(null)
      void deps.refetchCases()
    }
  }

  const handleRefresh = (): void => {
    void deps.refetchCases()
    void deps.refetchQuota()
  }

  return { handleCreateCase, handleCreateClient, handleOpenCase, handleUpdateCase, handleRefresh }
}

interface DashboardResources {
  casesByStatus: Record<CaseStatus, Case[]>
  clients: ReturnType<typeof useClientProfiles>['profiles']
  quota: ReturnType<typeof useCaseQuota>['quota']
  casesLoading: boolean
  clientsLoading: boolean
  quotaLoading: boolean
  casesError: string | null
  creating: boolean
  updating: boolean
  creatingClient: boolean
  mutationError: string | null
  createCase: ReturnType<typeof useCaseMutations>['create']
  updateCase: ReturnType<typeof useCaseMutations>['update']
  createClient: ReturnType<typeof useClientProfileMutations>['create']
  refetchCases: () => Promise<void>
  refetchClients: () => Promise<void>
  refetchQuota: () => Promise<void>
}

function useDashboardResources(): DashboardResources {
  const {
    casesByStatus,
    loading: casesLoading,
    error: casesError,
    refetch: refetchCases,
  } = useCasesGrouped()
  const {
    profiles: clients,
    loading: clientsLoading,
    refetch: refetchClients,
  } = useClientProfiles()
  const { quota, loading: quotaLoading, refetch: refetchQuota } = useCaseQuota()
  const {
    create: createCase,
    update: updateCase,
    creating,
    updating,
    error: mutationError,
  } = useCaseMutations()
  const { create: createClient, creating: creatingClient } = useClientProfileMutations()
  return {
    casesByStatus,
    clients,
    quota,
    casesLoading,
    clientsLoading,
    quotaLoading,
    casesError,
    creating,
    updating,
    creatingClient,
    mutationError,
    createCase,
    updateCase,
    createClient,
    refetchCases,
    refetchClients,
    refetchQuota,
  }
}

export function useCaseDashboard(): UseCaseDashboardReturn {
  const [viewMode, setViewModeState] = useState<ViewMode>(readStoredViewMode)
  const [statusTab, setStatusTabState] = useState<StatusTab>(readStoredStatusTab)
  const [isNewCaseModalOpen, setIsNewCaseModalOpen] = useState(false)
  const [isEditCaseModalOpen, setIsEditCaseModalOpen] = useState(false)
  const [selectedCase, setSelectedCase] = useState<Case | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const res = useDashboardResources()

  const setViewMode = useCallback((mode: ViewMode): void => {
    setViewModeState(mode)
    localStorage.setItem(VIEW_STORAGE_KEY, mode)
  }, [])

  const setStatusTab = useCallback((tab: StatusTab): void => {
    setStatusTabState(tab)
    localStorage.setItem(TAB_STORAGE_KEY, tab)
  }, [])

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, viewMode)
  }, [viewMode])
  useEffect(() => {
    localStorage.setItem(TAB_STORAGE_KEY, statusTab)
  }, [statusTab])

  const derived = useCaseDerivedData({
    casesByStatus: res.casesByStatus,
    searchQuery,
    statusTab,
    casesLoading: res.casesLoading,
  })

  const { handleCreateCase, handleCreateClient, handleOpenCase, handleUpdateCase, handleRefresh } =
    createCaseHandlers({
      createCase: res.createCase,
      updateCase: res.updateCase,
      createClient: res.createClient,
      refetchCases: res.refetchCases,
      refetchClients: res.refetchClients,
      refetchQuota: res.refetchQuota,
      allCases: derived.allCases,
      setIsNewCaseModalOpen,
      setIsEditCaseModalOpen,
      setSelectedCase,
    })

  return {
    viewMode,
    setViewMode,
    statusTab,
    setStatusTab,
    isNewCaseModalOpen,
    setIsNewCaseModalOpen,
    isEditCaseModalOpen,
    setIsEditCaseModalOpen,
    selectedCase,
    setSelectedCase,
    searchQuery,
    setSearchQuery,
    ...derived,
    clients: res.clients,
    quota: res.quota,
    casesLoading: res.casesLoading,
    clientsLoading: res.clientsLoading,
    quotaLoading: res.quotaLoading,
    isLoading: res.casesLoading || res.clientsLoading || res.quotaLoading,
    casesError: res.casesError,
    creating: res.creating,
    updating: res.updating,
    creatingClient: res.creatingClient,
    mutationError: res.mutationError,
    handleCreateCase,
    handleCreateClient,
    handleOpenCase,
    handleUpdateCase,
    handleRefresh,
  }
}
